import dataHubManager, { DataHubOperationType, DataHubStorageOperations } from '../../managers/data/DataHubManager'
import dataStorageManager, { BucketDefaultIDs } from '../../managers/data/DataStorageManager'
import { IArmador, OperatedArmador } from '../api-services/ArmadoresServices'
import { OperatedRepresentante } from '../api-services/RepresentantesServices'
import { RequestedChange } from '../api-services/ChangesServices'
import { IRequest, RequestObjectType, RequestStatus } from '../api-services/RequestsServices'
import { ITripulante } from '../api-services/TripulantesServices'
import { IUser } from '../api-services/UsersServices'
import { normalizeRut, rutify } from '../apiUtils'
import { esCompleto } from '../utils/usuarioCompletitud'
import { changesHubName } from './ChangesHub'
import dataHubServices, { defaultRefreshTime, StorageService } from './DataHubServices'

export enum ArmadoresHubPipelines {
  ARMADORES_WITH_REQUESTS = 'armadores_with_requests', // Para mostrar las solicitudes pendientes en el listado de armadores
  ARMADORES_WITH_CHANGES = 'armadores_with_changes', // Para mostrar los cambios pendientes en el listado de armadores
  ARMADORES_WITH_USER = 'armadores_with_user', // Para mostrar la información del usuario representante
  ARMADORES_WITH_TRIPULANTE = 'armadores_with_tripulante', // Para mostrar la información del tripulante en caso de que el representante sea un tripulante
  ARMADORES_WITH_REPRESENTANTE = 'armadores_with_representante', // Si el armador es representante
  ARMADORES_WITH_STATUS = 'armadores_with_status', // Para mostrar el estado del armador (activo, inactivo, etc)
  ARMADORES_WITH_REPRESENTANTES = 'armadores_with_representantes', // Si el armador tiene representantes
  ARMADORES_FULL = 'armadores_full', // Pipeline completo para mostrar toda la información relacionada a un armador (solicitudes, cambios, representante, asociaciones, etc)
}

export const armadoresHubName = 'armadoresHub'

export class ArmadoresHub implements StorageService {
  bucketData: any[] = []

  storageOperations: DataHubStorageOperations<IArmador> = {
    getData: () => [],
    markDirty: () => {},
    markStaled: () => {},
    forceRefresh: async () => {},
  }
  pipelinesOperations: Map<ArmadoresHubPipelines, DataHubStorageOperations<any>> = new Map()

  constructor() {}

  public async startup() {
    dataStorageManager.subscribeBucket(BucketDefaultIDs.ARMADORES, (data) => {
      this.bucketData = data
      this.storageOperations.forceRefresh()
    })
    this.storageOperations = dataHubManager.registerStorage(
      armadoresHubName,
      async () => {
        return this.bucketData
      },
      {
        invalidationStrategy: 'hybrid',
        checkTime: defaultRefreshTime,
        lazyLoad: true,
        pipelines: [
          {
            name: ArmadoresHubPipelines.ARMADORES_WITH_REQUESTS,
            operations: [
              {
                type: DataHubOperationType.MAP,
                name: 'addRequests',
                execute: (data: IArmador[]) => {
                  const dataWithRequests = this.addRequestToArmadores(data)
                  return dataWithRequests
                },
              },
            ],
          },
          {
            name: ArmadoresHubPipelines.ARMADORES_WITH_CHANGES,
            operations: [
              {
                type: DataHubOperationType.MAP,
                name: 'addChanges',
                execute: (data: OperatedArmador[]) => {
                  const dataWithChanges = this.addChangesToArmadores(data)
                  return dataWithChanges
                },
              },
            ],
          },
          {
            name: ArmadoresHubPipelines.ARMADORES_WITH_USER,
            operations: [
              {
                type: DataHubOperationType.MAP,
                name: 'addUser',
                execute: (data: OperatedArmador[]) => {
                  const dataWithUser = this.addUserToArmadores(data)
                  return dataWithUser
                },
              },
            ],
          },
          {
            name: ArmadoresHubPipelines.ARMADORES_WITH_TRIPULANTE,
            operations: [
              {
                type: DataHubOperationType.MAP,
                name: 'addTripulante',
                execute: (data: OperatedArmador[]) => {
                  const dataWithTripulante = this.addTripulanteToArmadores(data)
                  return dataWithTripulante
                },
              },
            ],
          },
          {
            name: ArmadoresHubPipelines.ARMADORES_WITH_REPRESENTANTE,
            operations: [
              {
                type: DataHubOperationType.MAP,
                name: 'addRepresentante',
                execute: (data: OperatedArmador[]) => {
                  const dataWithRepresentante = this.addRepresentanteToArmadores(data)
                  return dataWithRepresentante
                },
              },
            ],
          },
          {
            name: ArmadoresHubPipelines.ARMADORES_WITH_STATUS,
            operations: [
              {
                type: DataHubOperationType.MAP,
                name: 'addStatus',
                execute: (data: OperatedArmador[]) => {
                  const dataWithStatus = this.addStatusToArmadores(data)
                  return dataWithStatus
                },
              },
            ],
          },
          {
            name: ArmadoresHubPipelines.ARMADORES_WITH_REPRESENTANTES,
            operations: [
              {
                type: DataHubOperationType.MAP,
                name: 'addAssociations',
                execute: (data: OperatedArmador[]) => {
                  const dataWithAssociations = this.addRepresentantesToArmadores(data)
                  return dataWithAssociations
                },
              },
            ],
          },
        ],
      },
    )

    dataStorageManager.subscribeBucket(BucketDefaultIDs.SOLICITUDES, () => {
      this.storageOperations.forceRefresh()
    })
    dataStorageManager.subscribeBucket(BucketDefaultIDs.USERS, () => {
      this.storageOperations.forceRefresh()
    })
    dataStorageManager.subscribeBucket(BucketDefaultIDs.TRIPULANTES, () => {
      this.storageOperations.forceRefresh()
    })

    dataHubServices.subscribeToHub(changesHubName, {
      subscriberID: 'armadoresHub_changes_subscription',
      pipeline: null,
      callback: () => {
        this.storageOperations.forceRefresh()
      },
    })
  }

  private addRequestToArmadores(armadores: IArmador[]): Array<OperatedArmador> {
    const allRequests = dataStorageManager.getCurrentBucketData<IRequest>(BucketDefaultIDs.SOLICITUDES) || []
    const requestsList = allRequests.filter(
      (req) => req.tipo_objeto === RequestObjectType.Armador && req.tipo_objeto_2 === null,
    )
    const operatedArmadores: Array<OperatedArmador> = armadores.map((item) => {
      const request: IRequest | undefined = requestsList
        .filter((req) => req.id_objeto.toString() === item.id.toString())
        .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())[0] // Get latest request
      return {
        ...item,
        requestData: request,
        changesData: [],
        armadorUser: undefined,
        armadorTripulante: undefined,
        representantes: [],
        status: undefined,
      }
    })
    return operatedArmadores
  }

  private addChangesToArmadores(armadores: OperatedArmador[]) {
    const fullChangesHub = dataHubServices.getHub(changesHubName)
    const fullChangesList: Array<RequestedChange> = fullChangesHub ? fullChangesHub.storageOperations.getData() : []
    const operatedArmadores: Array<OperatedArmador> = armadores.map((item) => {
      const changesData = fullChangesList.filter((ch) => ch.id_objeto.toString() === item.id.toString())
      return {
        ...item,
        changesData: changesData,
      }
    })
    return operatedArmadores
  }

  private addUserToArmadores(armadores: OperatedArmador[]) {
    const usersList = dataStorageManager.getCurrentBucketData<IUser>(BucketDefaultIDs.USERS) || []
    const operatedArmadores: Array<OperatedArmador> = armadores.map((item) => {
      const armadorUser = usersList.find((u) => normalizeRut(u.rut) === normalizeRut(item.rut_representante))
      return {
        ...item,
        armadorUser: armadorUser,
      }
    })
    return operatedArmadores
  }

  private addTripulanteToArmadores(armadores: OperatedArmador[]) {
    const tripulantesList = dataStorageManager.getCurrentBucketData<ITripulante>(BucketDefaultIDs.TRIPULANTES) || []
    const operatedArmadores: Array<OperatedArmador> = armadores.map((item) => {
      const armadorTripulante = tripulantesList.find(
        (t) => normalizeRut(t.rut) === normalizeRut(item.rut_representante),
      )
      return {
        ...item,
        armadorTripulante: armadorTripulante,
      }
    })
    return operatedArmadores
  }

  private addRepresentanteToArmadores(armadores: OperatedArmador[]) {
    const operatedArmadores: Array<OperatedArmador> = armadores.map((item) => {
      const isRepresentante = armadores.some(
        (a) => normalizeRut(a.rut_representante) === normalizeRut(item.rut_representante) && a.id !== item.id,
      )
      return {
        ...item,
        isRepresentante: isRepresentante,
      }
    })
    return operatedArmadores
  }

  private addStatusToArmadores(armadores: OperatedArmador[]) {
    const operatedArmadores: Array<OperatedArmador> = armadores.map((item) => {
      let status = item.requestData ? item.requestData.estado : RequestStatus.INCOMPLETE
      const armadorUser = item.armadorUser
      const armadorTripulante = item.armadorTripulante
      // vigente=0: usuario deshabilitado, suprimir de pantallas operativas
      if (armadorUser && armadorUser.vigente === 0) {
        item.requestData = undefined
        item.changesData = []
        return { ...item, status: RequestStatus.INCOMPLETE }
      }
      const allRequests = dataStorageManager.getCurrentBucketData<IRequest>(BucketDefaultIDs.SOLICITUDES) || []
      const yaAprobado =
        item.requestData?.estado === RequestStatus.APPROVED ||
        allRequests.some(
          (s) =>
            ((s.tipo_objeto === RequestObjectType.Armador && String(s.id_objeto) === String(item.id)) ||
              (s.tipo_objeto === RequestObjectType.AutorizacionOperacion &&
                String(s.id_objeto_2) === String(item.id))) &&
            s.estado === RequestStatus.APPROVED,
        )
      // Completitud: fuente de verdad única compartida con TripulantesHub (ver usuarioCompletitud.ts).
      // Un armador natural que además opera como patrón requiere matrícula, igual que en la vista tripulante;
      // un tripulante/armador regular no la requiere. La completitud no depende de la perspectiva.
      const esPatron = armadorTripulante ? armadorTripulante.cargo === 'patron' : false
      const validDocs = esCompleto({
        tipoPersona: item.tipo_persona,
        idFotografia: armadorUser?.id_gestor_fotografia,
        idCarnet: armadorUser?.id_gestor_ci,
        esPatron: esPatron,
        idMatricula: armadorTripulante?.id_matricula,
        idPersoneria: item.id_personeria,
        yaAprobado: yaAprobado,
      })
      if (!validDocs) {
        status = RequestStatus.INCOMPLETE
        item.requestData = undefined
        item.changesData = []
      } else if (!item.requestData && yaAprobado) {
        const approvedReq = allRequests.find(
          (s) =>
            s.tipo_objeto === RequestObjectType.Armador &&
            String(s.id_objeto) === String(item.id) &&
            s.estado === RequestStatus.APPROVED,
        )
        if (approvedReq) {
          item.requestData = approvedReq
          status = RequestStatus.APPROVED
        }
      } else if (!item.requestData) {
        // Solicitud siempre visible: el armador está completo pero nunca se le generó la solicitud de
        // inscripción. Se sintetiza una solicitud VIRTUAL en estado PENDING para que el administrador la
        // vea y pueda aprobar/rechazar. No existe en el backend: al resolverla se materializa (insert)
        // desde RequestShower.saveChanges. Mismo patrón que TripulantesHub.buildVirtualTripulanteRequest.
        item.requestData = this.buildVirtualArmadorRequest(item)
        item.changesData = []
        status = RequestStatus.PENDING
      }
      if (item.requestData && item.requestData.estado === RequestStatus.PENDING) {
        item.changesData = []
      }
      return {
        ...item,
        status: status,
      }
    })
    return operatedArmadores
  }

  // Construye una solicitud de armador VIRTUAL (no persistida) en estado PENDING. id=0, esVirtual=true y
  // estado SIEMPRE PENDING porque la decisión de aprobar/rechazar es del administrador (al tomarla se
  // materializa en el backend con op_insert_solicitud desde RequestShower.saveChanges).
  private buildVirtualArmadorRequest(item: OperatedArmador): IRequest {
    return {
      id: 0,
      // El usuario del armador (representante legal/natural) es el solicitante natural de la inscripción.
      // Resolverlo aquí permite que el panel muestre el solicitante y evita el creador=0 inválido (FK).
      creador: item.armadorUser?.id_usuario ?? 0,
      revisor: null,
      estado: RequestStatus.PENDING,
      fecha_solicitud: new Date().toISOString(),
      fecha_respuesta: null,
      fecha_caducidad: new Date(new Date().getFullYear() + 1, 11, 31).toISOString(),
      tipo_objeto: RequestObjectType.Armador,
      id_objeto: item.id.toString(),
      tipo_objeto_2: null,
      id_objeto_2: null,
      razon_rechazo: '',
      esVirtual: true,
    }
  }

  private addRepresentantesToArmadores(armadores: OperatedArmador[]) {
    const allRequests = dataStorageManager.getCurrentBucketData<IRequest>(BucketDefaultIDs.SOLICITUDES) || []
    const requestsList = allRequests.filter((req) => req.tipo_objeto === RequestObjectType.RepresentanteArmador)
    const operatedArmadores: Array<OperatedArmador> = armadores.map((item) => {
      const representantes: OperatedRepresentante[] = []
      // Ambos tipos (natural y jurídico) pueden tener representantes
      {
        const repsRequests = requestsList.filter(
          (req) =>
            req.tipo_objeto === RequestObjectType.RepresentanteArmador &&
            req.id_objeto.toString() === item.id.toString(),
        )
        repsRequests.forEach((req) => {
          const representanteUser = dataStorageManager.searchDataInBucket<IUser>(
            BucketDefaultIDs.USERS,
            (u) => req.id_objeto_2 != null && u.id_usuario.toString() === req.id_objeto_2.toString(),
          )
          if (representanteUser) {
            const representanteArmador = dataStorageManager.searchDataInBucket<IArmador>(
              BucketDefaultIDs.ARMADORES,
              (a) => rutify(a.rut_representante) === rutify(representanteUser.rut),
            )
            const representanteTripulante = dataStorageManager.searchDataInBucket<ITripulante>(
              BucketDefaultIDs.TRIPULANTES,
              (t) => rutify(t.rut) === rutify(representanteUser.rut),
            )
            representantes.push({
              ...representanteUser,
              id_empresa: item.id,
              id_solicitud: req.id,
              usuarioArmadorData: representanteArmador,
              usuarioTripulanteData: representanteTripulante,
              solicitudData: req,
              representados: [],
              representadosData: [],
              usuarioPersonalRequests: [],
            } as unknown as OperatedRepresentante)
          }
        })
      }
      return {
        ...item,
        representantes: representantes,
      }
    })
    return operatedArmadores
  }
}

/*
const requestsList: Ref<IRequest[]> = ref([])
const changesRequests: Ref<IRequest[]> = ref([])
const asociationsRequests: Ref<IRequest[]> = ref([])
const representantesRequests: Ref<IRequest[]> = ref([])
const changesList: Ref<IChange[]> = ref([])
const fullChangesList: Ref<RequestedChange[]> = ref([])

const { showList, fullList, reload, isLoading, filters, statusFilter, sorting, pagination, error, performAction } =
  useComplexBucketList<OperatedArmador, IArmador>(BucketDefaultIDs.ARMADORES, (item) => {
    const armadorUser = dataStorageManager.searchDataInBucket<IUser>(
      BucketDefaultIDs.USERS,
      (u) => rutify(u.rut) === rutify(item.rut_representante),
    )
    const armadorTripulante = dataStorageManager.searchDataInBucket<ITripulante>(
      BucketDefaultIDs.TRIPULANTES,
      (t) => rutify(t.rut) === rutify(item.rut_representante),
    )
    // Detectar si hay cambios pendientes que apliquen a los documentos obligatorios

    let status = request ? request.estado : RequestStatus.INCOMPLETE
    const fotografiaValid =
      item.tipo_persona === 'natural' ? (armadorUser ? armadorUser.id_gestor_fotografia !== 0 : false) : true
    const ciValid = item.tipo_persona === 'natural' ? (armadorUser ? armadorUser.id_gestor_ci !== 0 : false) : true
    const matriculaValid = armadorTripulante ? armadorTripulante.id_matricula !== 0 : true
    const personeriaValid = item.tipo_persona == 'juridica' ? item.id_personeria !== 0 : true
    const validDocs = fotografiaValid && ciValid && matriculaValid && personeriaValid
    if (!validDocs) {
      status = RequestStatus.INCOMPLETE
      request = undefined
      changesData = []
    }
    if (request && request.estado === RequestStatus.PENDING) {
      changesData = []
    }

    const representantes: OperatedRepresentante[] = []
    if (item.tipo_persona === 'juridica') {
      const repsRequests = representantesRequests.value.filter(
        (req) =>
          req.tipo_objeto === RequestObjectType.RepresentanteArmador &&
          req.id_objeto.toString() === item.id.toString(),
      )
      repsRequests.forEach((req) => {
        const representanteUser = dataStorageManager.searchDataInBucket<IUser>(
          BucketDefaultIDs.USERS,
          (u) => req.id_objeto_2 != null && u.id_usuario.toString() === req.id_objeto_2.toString(),
        )
        if (representanteUser) {
          const representanteArmador = dataStorageManager.searchDataInBucket<IArmador>(
            BucketDefaultIDs.ARMADORES,
            (a) => rutify(a.rut_representante) === rutify(representanteUser.rut),
          )
          const representanteTripulante = dataStorageManager.searchDataInBucket<ITripulante>(
            BucketDefaultIDs.TRIPULANTES,
            (t) => rutify(t.rut) === rutify(representanteUser.rut),
          )
          representantes.push({
            id_empresa: item.id,
            id_solicitud: req.id,
            id_usuario: representanteUser.id_usuario,
            empresaData: item,
            usuarioData: representanteUser,
            usuarioArmadorData: representanteArmador,
            usuarioTripulanteData: representanteTripulante,
            solicitudData: req,
          })
        }
      })
    }

    return {
      ...item,
      requestData: request,
      changesData: changesData,
      armadorUser: armadorUser,
      armadorTripulante: armadorTripulante,
      representantes: representantes,
      status: status,
    }
  })

  dataStorageManager.subscribeBucket(BucketDefaultIDs.USERS, () => {
    reload.value = uuid()
  })
  dataStorageManager.subscribeBucket(BucketDefaultIDs.TRIPULANTES, () => {
    reload.value = uuid()
  })

  dataStorageManager.subscribeBucket(BucketDefaultIDs.SOLICITUDES, onRequestsLoad)
  dataStorageManager.subscribeBucket(BucketDefaultIDs.CAMBIOS, onChangesLoad)

function onRequestsLoad(requests: Array<IRequest>) {
  requestsList.value = requests.filter((r) => r.tipo_objeto === RequestObjectType.Armador && r.tipo_objeto_2 === null)
  changesRequests.value = requests.filter((r) => r.tipo_objeto === RequestObjectType.Modificacion)
  asociationsRequests.value = requests.filter((r) => r.tipo_objeto === RequestObjectType.Asociacion_embarcacion)
  representantesRequests.value = requests.filter((r) => r.tipo_objeto === RequestObjectType.RepresentanteArmador)
  reload.value = uuid()
  loadFullChangesList()
}

function onChangesLoad(changes: Array<IChange>) {
  changesList.value = changes.filter((c) => c.log_cambios.includes(':') && c.modulo === ChangeModule.Armador) // Filter valid changes
  loadFullChangesList()
}

function loadFullChangesList() {
  if (changesList.value.length > 0 && changesRequests.value.length > 0) {
    fullChangesList.value = changesList.value
      .map((change) => {
        const requestData = changesRequests.value
          .filter((req) => req.id_objeto.toString() === change.id.toString())
          .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())[0]

        return {
          ...change,
          requestData: requestData,
        }
      })
      .filter((rc) => rc.requestData !== undefined)
      .filter((rc) => rc.requestData!.estado === RequestStatus.PENDING) as RequestedChange[] // Only pending changes
    reload.value = uuid()
  }
}*/
